import {useMemo, useRef} from 'react';
import {useQuery} from '@shopify/react-graphql';
import {useShop} from '@web-utilities/shop';
import {Portal} from '@shopify/polaris';
import {validLocalesForMarket} from 'utilities/helper';
import {useShopLocalesAndMarkets} from 'utilities/use-shop-locales-and-markets';

import PreviewQuery from './graphql/PreviewQuery.graphql';

interface Return {
  loading: boolean;
  canPreview: boolean;
  preview(): void;
  previewForm: JSX.Element;
}

interface LocalesAndRegions {
  locales: string[];
  regions: string[];
}

export function usePreview(): Return {
  const previewFormRef = useRef<HTMLFormElement>(null);
  const {currentLocale, markets, currentMarket} = useShopLocalesAndMarkets();
  const {shopDomain} = useShop();
  const {data, loading} = useQuery(PreviewQuery, {
    ssr: false,
  });
  const storeDigest = data?.urlWithPasswordBypass;
  const primaryDomain = data?.shop.primaryDomain?.url;
  const previewSupported = data?.shop.features.storefront;
  const localesAndRegionsForMarkets = useMemo(() => {
    return markets.reduce<{
      [key: string]: LocalesAndRegions;
    }>((acc, market) => {
      const locales = validLocalesForMarket(market, markets);
      const localesAndRegions = {
        locales,
        regions: market?.regions?.map(({code}) => code) || [],
      };
      return {...acc, [market.id]: localesAndRegions};
    }, {});
  }, [markets]);
  const targetMarketForPreview = useMemo(() => {
    if (!currentMarket) {
      // Always try to preview the current language in the primary market
      const primaryMarket = markets.find(({primary}) => primary);
      if (
        primaryMarket &&
        localesAndRegionsForMarkets[primaryMarket.id].locales.includes(
          currentLocale.locale,
        )
      ) {
        return primaryMarket;
      }

      // Worst case preview in the first market that supports the current language
      const firstMarketWithLocale = markets.find((market) =>
        localesAndRegionsForMarkets[market.id].locales.includes(
          currentLocale.locale,
        ),
      );
      if (firstMarketWithLocale) return firstMarketWithLocale;

      return;
    }

    return currentMarket;
  }, [localesAndRegionsForMarkets, currentMarket, currentLocale, markets]);
  const canPreview = Boolean(
    storeDigest && previewSupported && primaryDomain && targetMarketForPreview,
  );

  const previewForm = (
    <Portal idPrefix="PreviewForm">
      <form
        action={`https://${shopDomain}/localization`}
        acceptCharset="UTF-8"
        method="post"
        target="_blank"
        aria-hidden
        style={{display: 'none'}}
        ref={previewFormRef}
      >
        <input type="hidden" name="_method" value="PUT" />
        <input type="hidden" name="return_to" value={storeDigest || ''} />
        {targetMarketForPreview && (
          <input
            type="hidden"
            id="preview-country-code"
            name="country_code"
            value={
              localesAndRegionsForMarkets[targetMarketForPreview.id]
                ?.regions[0] || ''
            }
          />
        )}
        <input
          type="hidden"
          id="preview-language-code"
          name="language_code"
          value={currentLocale.locale}
        />
      </form>
    </Portal>
  );

  const preview = () => {
    if (!canPreview) return;

    try {
      previewFormRef?.current?.submit();
    } catch {
      // We can be potentially be blocked by pop-up blockers and
      // a generic Exception is thrown by Firefox when that happens
      // but we don't want to crash the page
    }
  };

  return {
    loading,
    canPreview,
    preview,
    previewForm,
  };
}
